import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ChevronDown,
  FolderInput,
  FolderOpen,
  Loader2,
  MoreHorizontal,
  Music,
  Pencil,
  Plus,
  Search,
  Trash2,
  Upload,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useMyUserMediaQuery,
  useMyUserMediaFoldersQuery,
  useCreateMyUserMediaMutation,
  useUpdateMyUserMediaMutation,
  useDeleteMyUserMediaMutation,
  useCreateMyUserMediaFolderMutation,
  useUpdateMyUserMediaFolderMutation,
  useDeleteMyUserMediaFolderMutation,
} from "@/generated/graphql";
import { useUploadFile } from "@/hooks/use-upload-file";
import { probeImageSize } from "@/lib/layers/probeImageSize";
import { probeMediaMetadata } from "@/lib/layers/probeMediaMetadata";
import { layerTypeConfig } from "@/lib/layers/layerTypeConfig";

type MediaKindFilter = "all" | "image" | "video" | "audio";
type FolderFilter = "all" | "root" | string;

interface MediaRow {
  id: string;
  url: string;
  kind: string;
  name?: string | null;
  folder_id?: string | null;
}

interface FolderRow {
  id: string;
  name: string;
  media_count: number;
}

const ALL_UPLOAD_MIME_TYPES = [
  ...layerTypeConfig.image.acceptMimeTypes,
  ...layerTypeConfig.video.acceptMimeTypes,
  ...layerTypeConfig.audio.acceptMimeTypes,
];

function resolveUploadKind(mimeType: string): "image" | "video" | "audio" | null {
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  if (mimeType.startsWith("audio/")) return "audio";
  return null;
}

const chipClass = (active: boolean) =>
  `text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
    active
      ? "border-primary bg-primary/10 text-primary"
      : "border-border bg-muted/30 hover:bg-muted/50"
  }`;

const KIND_FILTERS: MediaKindFilter[] = ["all", "image", "video", "audio"];

// No total count comes back from the API, so "is there another page" is a
// heuristic: a full page (exactly PAGE_SIZE rows) implies there may be more.
const PAGE_SIZE = 60;
const FOLDER_NAME_MAX_LENGTH = 80;

const MediaThumb: React.FC<{ row: MediaRow }> = ({ row }) => {
  const [hovered, setHovered] = useState(false);

  if (row.kind === "video") {
    return (
      <div
        className="aspect-video bg-black/40"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {hovered ? (
          <video
            key="hover"
            src={row.url}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover pointer-events-none"
          />
        ) : (
          <video
            key="still"
            src={row.url}
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover pointer-events-none"
          />
        )}
      </div>
    );
  }

  if (row.kind === "audio") {
    return (
      <div className="aspect-video flex items-center justify-center bg-muted/50">
        <Music className="w-6 h-6 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="aspect-video bg-black/10">
      <img
        src={row.url}
        alt={row.name ?? ""}
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </div>
  );
};

const MyMediaPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<MediaKindFilter>("all");
  const [folderFilter, setFolderFilter] = useState<FolderFilter>("all");
  const [isUploading, setIsUploading] = useState(false);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameFolderTarget, setRenameFolderTarget] = useState<FolderRow | null>(null);
  const [renameFolderName, setRenameFolderName] = useState("");
  const [deleteFolderTarget, setDeleteFolderTarget] = useState<FolderRow | null>(null);

  const [renameItemTarget, setRenameItemTarget] = useState<MediaRow | null>(null);
  const [renameItemName, setRenameItemName] = useState("");
  const [deleteItemTarget, setDeleteItemTarget] = useState<MediaRow | null>(null);

  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { getUploadUrl } = useUploadFile();
  const [createMedia] = useCreateMyUserMediaMutation();
  const [updateMedia, { loading: updatingItem }] = useUpdateMyUserMediaMutation();
  const [deleteMedia, { loading: deletingItem }] = useDeleteMyUserMediaMutation();
  const [createFolder, { loading: creatingFolder }] = useCreateMyUserMediaFolderMutation();
  const [updateFolder, { loading: renamingFolder }] = useUpdateMyUserMediaFolderMutation();
  const [deleteFolder, { loading: deletingFolder }] = useDeleteMyUserMediaFolderMutation();

  const { data: foldersData, refetch: refetchFolders } = useMyUserMediaFoldersQuery();
  const folders: FolderRow[] = useMemo(
    () => foldersData?.myUserMediaFolders ?? [],
    [foldersData],
  );

  // Only present filter keys the backend cares about — an explicit
  // `undefined` value still serializes onto the wire and defeats
  // MockedProvider-style exact-variable matching in tests, so filters map to
  // absent keys instead. limit/offset are always present: this is the base
  // (first-page) query — pagination beyond it goes through fetchMore.
  const queryVariables = useMemo(() => {
    const vars: { kind?: string; folderId?: string; search?: string; limit: number; offset: number } = {
      limit: PAGE_SIZE,
      offset: 0,
    };
    if (kindFilter !== "all") vars.kind = kindFilter;
    if (folderFilter !== "all") vars.folderId = folderFilter;
    if (debouncedSearch) vars.search = debouncedSearch;
    return vars;
  }, [kindFilter, folderFilter, debouncedSearch]);

  const { data, loading, error, refetch, fetchMore } = useMyUserMediaQuery({
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });
  const media: MediaRow[] = useMemo(() => data?.myUserMedia ?? [], [data]);

  // Any filter change starts a fresh first page — the base query above
  // already re-fires at offset 0 (which the myUserMedia cache merge policy
  // treats as "replace"), so this just clears the "no more pages" flag from
  // whatever the previous filter's list ended on.
  useEffect(() => {
    setHasMore(true);
  }, [kindFilter, folderFilter, debouncedSearch]);

  // No total count comes back from the API — a page shorter than PAGE_SIZE
  // is the only signal that we've reached the end.
  useEffect(() => {
    if (!loading && media.length > 0 && media.length < PAGE_SIZE) {
      setHasMore(false);
    }
  }, [loading, media.length]);

  const handleLoadMore = async () => {
    if (isFetchingMoreRef.current || loading || error || !hasMore) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const prevCount = media.length;
      const res = await fetchMore({
        variables: { ...queryVariables, offset: prevCount },
      });
      const fetched = res.data?.myUserMedia?.length ?? 0;
      if (fetched < PAGE_SIZE) setHasMore(false);
    } catch (err) {
      // hasMore stays true, so the button remains for a retry.
      console.error("Failed to load more media", err);
      toast({ title: t("myMedia.toast.itemError"), variant: "destructive" });
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  };

  const refetchAll = () => {
    // Mutations (rename/move/delete/upload) reset back to the first page —
    // simplest way to keep pagination consistent without reconciling which
    // page an edited row used to live on. The base query is already pinned
    // at offset 0, so a plain refetch() collapses the accumulated pages back
    // to just page one via the cache merge policy.
    setHasMore(true);
    refetch();
    refetchFolders();
  };

  const handleFilesSelected = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    setIsUploading(true);
    const targetFolderId =
      folderFilter !== "all" && folderFilter !== "root" ? folderFilter : undefined;
    try {
      // Each file runs inside its own try/catch: one bad file or a network
      // blip must not abort the rest of the batch, and the refetch in the
      // outer finally must always run so files that DID make it become
      // visible even when a later one fails.
      for (const file of Array.from(fileList)) {
        const kind = resolveUploadKind(file.type);
        if (!kind) {
          toast({
            title: t("myMedia.toast.unsupportedType"),
            description: file.name,
            variant: "destructive",
          });
          continue;
        }

        try {
          const blobUrl = URL.createObjectURL(file);
          let width: number | undefined;
          let height: number | undefined;
          let duration: number | undefined;
          try {
            if (kind === "image") {
              const size = await probeImageSize(blobUrl);
              if (size.width > 0 && size.height > 0) {
                width = size.width;
                height = size.height;
              }
            } else {
              const meta = await probeMediaMetadata(blobUrl, kind);
              if (meta.width > 0 && meta.height > 0) {
                width = meta.width;
                height = meta.height;
              }
              if (meta.duration > 0) duration = meta.duration;
            }
          } finally {
            URL.revokeObjectURL(blobUrl);
          }

          const uploadedUrl = await getUploadUrl(file, undefined, {
            acceptedMimeTypes: layerTypeConfig[kind].acceptMimeTypes,
          });
          if (!uploadedUrl) {
            toast({
              title: t("myMedia.toast.uploadError"),
              description: file.name,
              variant: "destructive",
            });
            continue;
          }

          await createMedia({
            variables: {
              input: {
                url: uploadedUrl,
                kind,
                name: file.name,
                ...(width ? { width } : {}),
                ...(height ? { height } : {}),
                ...(duration ? { duration } : {}),
                ...(targetFolderId ? { folderId: targetFolderId } : {}),
              },
            },
          });
        } catch (err) {
          console.error(`Failed to upload media "${file.name}"`, err);
          toast({
            title: t("myMedia.toast.uploadError"),
            description: file.name,
            variant: "destructive",
          });
        }
      }
    } finally {
      refetchAll();
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      await createFolder({ variables: { name: newFolderName.trim() } });
      setNewFolderName("");
      setNewFolderOpen(false);
      refetchFolders();
    } catch (err) {
      console.error("Failed to create folder", err);
      toast({ title: t("myMedia.toast.folderError"), variant: "destructive" });
    }
  };

  const handleRenameFolderSubmit = async () => {
    if (!renameFolderTarget || !renameFolderName.trim()) return;
    try {
      await updateFolder({
        variables: { id: renameFolderTarget.id, name: renameFolderName.trim() },
      });
      setRenameFolderTarget(null);
      refetchFolders();
    } catch (err) {
      console.error("Failed to rename folder", err);
      toast({ title: t("myMedia.toast.folderError"), variant: "destructive" });
    }
  };

  const handleDeleteFolderConfirm = async () => {
    if (!deleteFolderTarget) return;
    try {
      const wasActive = folderFilter === deleteFolderTarget.id;
      await deleteFolder({ variables: { id: deleteFolderTarget.id } });
      setDeleteFolderTarget(null);
      if (wasActive) setFolderFilter("all");
      refetchAll();
    } catch (err) {
      console.error("Failed to delete folder", err);
      toast({ title: t("myMedia.toast.folderError"), variant: "destructive" });
    }
  };

  const handleRenameItemSubmit = async () => {
    if (!renameItemTarget || !renameItemName.trim()) return;
    try {
      await updateMedia({
        variables: { id: renameItemTarget.id, input: { name: renameItemName.trim() } },
      });
      setRenameItemTarget(null);
      // refetchAll, not plain refetch(): the offset-0 refetch collapses the
      // accumulated list back to page one, so hasMore must reset with it.
      refetchAll();
    } catch (err) {
      console.error("Failed to rename media", err);
      toast({ title: t("myMedia.toast.itemError"), variant: "destructive" });
    }
  };

  const handleMoveItem = async (mediaId: string, targetFolderId: string | null) => {
    try {
      await updateMedia({ variables: { id: mediaId, input: { folderId: targetFolderId } } });
      refetchAll();
    } catch (err) {
      console.error("Failed to move media", err);
      toast({ title: t("myMedia.toast.itemError"), variant: "destructive" });
    }
  };

  const handleDeleteItemConfirm = async () => {
    if (!deleteItemTarget) return;
    try {
      await deleteMedia({ variables: { id: deleteItemTarget.id } });
      setDeleteItemTarget(null);
      refetchAll();
    } catch (err) {
      console.error("Failed to delete media", err);
      toast({ title: t("myMedia.toast.itemError"), variant: "destructive" });
    }
  };

  const hasActiveFilters = kindFilter !== "all" || folderFilter !== "all" || !!debouncedSearch;

  return (
    <Layout>
      <div className="w-full p-6 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold">{t("myMedia.title")}</h1>
            <p className="text-muted-foreground mt-1">{t("myMedia.subtitle")}</p>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALL_UPLOAD_MIME_TYPES.join(",")}
              className="hidden"
              data-testid="my-media-file-input"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            <Button
              data-testid="my-media-upload"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {isUploading ? t("myMedia.uploading") : t("myMedia.upload")}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("myMedia.searchPlaceholder")}
              className="pl-8"
            />
          </div>
          <div className="flex gap-1.5">
            {KIND_FILTERS.map((k) => (
              <button
                key={k}
                type="button"
                className={chipClass(kindFilter === k)}
                onClick={() => setKindFilter(k)}
              >
                {t(`myMedia.kind.${k}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            type="button"
            data-testid="my-media-folder-all"
            className={chipClass(folderFilter === "all")}
            onClick={() => setFolderFilter("all")}
          >
            {t("myMedia.folders.all")}
          </button>
          <button
            type="button"
            data-testid="my-media-folder-root"
            className={chipClass(folderFilter === "root")}
            onClick={() => setFolderFilter("root")}
          >
            {t("myMedia.folders.root")}
          </button>
          {folders.map((folder) => (
            <div key={folder.id} className="relative inline-flex items-center">
              <button
                type="button"
                data-testid={`my-media-folder-${folder.id}`}
                className={chipClass(folderFilter === folder.id)}
                onClick={() => setFolderFilter(folder.id)}
              >
                {folder.name} ({folder.media_count})
              </button>
              {folderFilter === folder.id && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="ml-0.5 p-0.5 rounded-full hover:bg-muted/70 text-muted-foreground"
                      aria-label={t("myMedia.folders.options")}
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem
                      onClick={() => {
                        setRenameFolderTarget(folder);
                        setRenameFolderName(folder.name);
                      }}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      {t("myMedia.folders.rename")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive"
                      onClick={() => setDeleteFolderTarget(folder)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t("myMedia.folders.delete")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setNewFolderOpen(true)}
          >
            <Plus className="w-3.5 h-3.5 mr-1" />
            {t("myMedia.folders.new")}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="py-16 text-center space-y-3">
            <p className="text-muted-foreground">{t("myMedia.errorLoading")}</p>
            <Button variant="secondary" size="sm" onClick={() => refetch()}>
              {t("myMedia.retry")}
            </Button>
          </div>
        ) : media.length === 0 ? (
          <div className="py-16 text-center">
            <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">
              {hasActiveFilters ? t("myMedia.noResults") : t("myMedia.empty")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {media.map((row) => (
              <div key={row.id} className="min-w-0" data-testid={`my-media-item-${row.id}`}>
                <div className="relative rounded-md overflow-hidden border border-border">
                  <MediaThumb row={row} />
                  <div className="absolute top-1 right-1">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="secondary"
                          size="icon"
                          className="h-7 w-7 bg-background/80 hover:bg-background"
                          data-testid={`my-media-item-menu-${row.id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => {
                            setRenameItemTarget(row);
                            setRenameItemName(row.name ?? "");
                          }}
                        >
                          <Pencil className="w-4 h-4 mr-2" />
                          {t("myMedia.item.rename")}
                        </DropdownMenuItem>
                        <DropdownMenuSub>
                          <DropdownMenuSubTrigger>
                            <FolderInput className="w-4 h-4 mr-2" />
                            {t("myMedia.item.moveTo")}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuSubContent>
                            <DropdownMenuItem
                              disabled={!row.folder_id}
                              onClick={() => handleMoveItem(row.id, null)}
                            >
                              {t("myMedia.folders.root")}
                            </DropdownMenuItem>
                            {folders.length > 0 && <DropdownMenuSeparator />}
                            {folders.map((folder) => (
                              <DropdownMenuItem
                                key={folder.id}
                                disabled={row.folder_id === folder.id}
                                onClick={() => handleMoveItem(row.id, folder.id)}
                              >
                                {folder.name}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuSubContent>
                        </DropdownMenuSub>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setDeleteItemTarget(row)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {t("myMedia.item.delete")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <p
                  className="mt-1 text-xs text-muted-foreground truncate"
                  title={row.name ?? ""}
                >
                  {row.name || t("myMedia.item.untitled")}
                </p>
              </div>
            ))}
          </div>
        )}

        {!loading && !error && media.length > 0 && hasMore && (
          <div className="pt-2 flex justify-center">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              data-testid="my-media-load-more"
              disabled={isFetchingMore}
              onClick={handleLoadMore}
            >
              {isFetchingMore && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t("myMedia.loadMore")}
            </Button>
          </div>
        )}
      </div>

      {/* New folder */}
      <Dialog
        open={newFolderOpen}
        onOpenChange={(open) => {
          setNewFolderOpen(open);
          if (!open) setNewFolderName("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("myMedia.folders.newTitle")}</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="my-media-new-folder-name">{t("myMedia.folders.nameLabel")}</Label>
            <Input
              id="my-media-new-folder-name"
              autoFocus
              className="mt-2"
              maxLength={FOLDER_NAME_MAX_LENGTH}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder={t("myMedia.folders.namePlaceholder")}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateFolder();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewFolderOpen(false)}>
              {t("myMedia.cancel")}
            </Button>
            <Button
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || creatingFolder}
            >
              {creatingFolder ? t("myMedia.folders.creating") : t("myMedia.folders.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename folder */}
      <Dialog
        open={!!renameFolderTarget}
        onOpenChange={(open) => !open && setRenameFolderTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("myMedia.folders.renameTitle")}</DialogTitle>
          </DialogHeader>
          <div>
            <Label htmlFor="my-media-rename-folder-name">
              {t("myMedia.folders.nameLabel")}
            </Label>
            <Input
              id="my-media-rename-folder-name"
              autoFocus
              className="mt-2"
              maxLength={FOLDER_NAME_MAX_LENGTH}
              value={renameFolderName}
              onChange={(e) => setRenameFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRenameFolderSubmit();
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameFolderTarget(null)}>
              {t("myMedia.cancel")}
            </Button>
            <Button
              onClick={handleRenameFolderSubmit}
              disabled={!renameFolderName.trim() || renamingFolder}
            >
              {renamingFolder ? t("myMedia.saving") : t("myMedia.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete folder confirm */}
      <AlertDialog
        open={!!deleteFolderTarget}
        onOpenChange={(open) => !open && setDeleteFolderTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("myMedia.folders.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("myMedia.folders.confirmDeleteBody", { name: deleteFolderTarget?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("myMedia.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="my-media-delete-folder-confirm"
              onClick={handleDeleteFolderConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletingFolder}
            >
              {deletingFolder ? t("myMedia.deleting") : t("myMedia.folders.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rename item */}
      <Dialog
        open={!!renameItemTarget}
        onOpenChange={(open) => !open && setRenameItemTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("myMedia.item.renameTitle")}</DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={renameItemName}
            onChange={(e) => setRenameItemName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameItemSubmit();
            }}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameItemTarget(null)}>
              {t("myMedia.cancel")}
            </Button>
            <Button
              onClick={handleRenameItemSubmit}
              disabled={!renameItemName.trim() || updatingItem}
            >
              {updatingItem ? t("myMedia.saving") : t("myMedia.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete item confirm */}
      <AlertDialog
        open={!!deleteItemTarget}
        onOpenChange={(open) => !open && setDeleteItemTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("myMedia.item.confirmDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("myMedia.item.confirmDeleteBody", {
                name: deleteItemTarget?.name || t("myMedia.item.untitled"),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("myMedia.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="my-media-delete-item-confirm"
              onClick={handleDeleteItemConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletingItem}
            >
              {deletingItem ? t("myMedia.deleting") : t("myMedia.item.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};

export default MyMediaPage;
